import { RangeSlider } from '@mantine/core'
import React, { useEffect, useState } from 'react'
import { useRange } from 'react-instantsearch'
import { Input } from 'components/Input'
import { Box, Flex } from 'components/Layout'
import { Text } from 'components/Typography'
import { FONT_SIZE } from 'Theme'
import { FilterCollapsibleItem } from './FilterCollapsibleItem'

type FilterCollapsiblePriceRangeProps = {
  title: string
  defaultOpened?: boolean
  attribute: string
}

export const FilterCollapsiblePriceRange = ({
  title,
  defaultOpened,
  attribute,
}: FilterCollapsiblePriceRangeProps) => {
  const { start, range, canRefine, refine } = useRange({ attribute })
  const { min, max } = range

  const from = Math.max(min, Number.isFinite(start[0]) ? start[0] : min)
  const to = Math.min(max, Number.isFinite(start[1]) ? start[1] : max)

  const [value, setValue] = useState<[number, number]>([from, to])

  const debounceRef = React.useRef<NodeJS.Timeout | null>(null)

  const handleInputChange = (index: 0 | 1, newVal: number) => {
    const next = [...value] as [number, number]
    next[index] = newVal
    setValue(next)

    // debounce refinement
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }
    debounceRef.current = setTimeout(() => {
      refine(next)
    }, 2000) // 2 seconds debounce
  }

  useEffect(() => {
    setValue([from, to])
  }, [from, to])

  const handleInputBlur = () => {
    refine(value)
  }

  if (!canRefine) {
    return null
  }

  return (
    <FilterCollapsibleItem title={title} defaultOpened={defaultOpened}>
      <Box>
        <Flex justifyContent="space-between" mb={2}>
          <Text>${min}</Text>
          <Text>${max}</Text>
        </Flex>

        <RangeSlider
          label={null}
          min={min}
          max={max}
          value={value}
          onChange={setValue}
          onChangeEnd={refine}
          disabled={!canRefine}
          step={10}
        />

        <Flex
          justifyContent="space-between"
          alignItems="center"
          mt={12}
          gap={40}
        >
          <Box width="100%">
            <Text fontSize={FONT_SIZE.PX_12}>Min price (USD)</Text>
            <Input
              type="number"
              min={min}
              max={value[1]}
              value={value[0]}
              onChange={(e) => handleInputChange(0, Number(e.target.value))}
              onBlur={handleInputBlur}
              disabled={!canRefine}
              style={{ display: 'flex' }}
            />
          </Box>
          <Box width="100%">
            <Text as="p" fontSize={FONT_SIZE.PX_12}>
              Max price (USD)
            </Text>
            <Input
              type="number"
              min={value[0]}
              max={max}
              value={value[1]}
              onChange={(e) => handleInputChange(1, Number(e.target.value))}
              onBlur={handleInputBlur}
              disabled={!canRefine}
              style={{ display: 'flex' }}
            />
          </Box>
        </Flex>
      </Box>
    </FilterCollapsibleItem>
  )
}
